import type { GeoLine, MapObject, PinFolder } from './types'

/** Resolves a pin type id to its display name; the identity labeler leaves the
 *  id as is. A rendering caller passes the pin-type catalog's `label` so the
 *  list sorts and reads in the user's language. Mirrors
 *  {@link DEFAULT_LINE_LABELS}. */
export type PinTypeLabeler = (type: string) => string
const IDENTITY_LABELER: PinTypeLabeler = (type) => type

/**
 * CLUB-676: grouping a collection's pins and lines by folder, for the folder
 * tree. Pure — no React, no fetching — so the ordering and the "which pins fall
 * outside a folder" rules can be tested on their own.
 *
 * This is a port of the app's `buildPinCollectionDetailView`
 * (`domains/clubhouse/wehunt-mobile/.../pin_collection_detail_view.dart`). The
 * point of the ticket is that both platforms present the same data the same
 * way, so where the app's behaviour is a deliberate rule it is matched here
 * rather than re-decided. The one place this deliberately differs is search —
 * see {@link filterByName}.
 */

/**
 * How a collection's pins are ordered. `custom` follows each pin's stored
 * `orderIndex` (the order the hunter dragged them into on the phone);
 * `ascending`/`descending` sort by display name.
 */
export type PinSortOrder = 'custom' | 'ascending' | 'descending'

/** One folder together with the pins and lines filed under it. */
export type PinFolderContents = {
  folder: PinFolder
  pins: MapObject[]
  lines: GeoLine[]
}

/**
 * One collection's contents: its own folders, then whatever it holds outside
 * them.
 *
 * There are exactly two, and they are kept apart rather than merged because
 * they answer different questions. The team's collection is the ground's — every
 * member sees the same one. The viewer's own follows the signed-in hunter across
 * every team they belong to and is not ground-filtered at all, so a pin in it
 * may be hundreds of kilometres from the ground on screen. Counting the two as
 * one number is what made the Map tab claim "15 pins on this ground" above a
 * chip reading 3: the other 12 were the viewer's own.
 */
export type PinFolderCollection = {
  /** True for the viewer's own collection, false for the team's. */
  private: boolean
  folders: PinFolderContents[]
  /** Pins in this collection that belong to no folder — see
   *  {@link buildPinFolderView}. */
  ungroupedPins: MapObject[]
  ungroupedLines: GeoLine[]
}

export type PinFolderView = {
  team: PinFolderCollection
  /** The signed-in hunter's own collection. Pins only — a line carries no
   *  owner, so it can only ever be the team's. */
  mine: PinFolderCollection
  /** The order actually applied, which is not always the one requested — see
   *  {@link buildPinFolderView}. One order for both collections: the sort
   *  control is a single select above them both. */
  sortOrder: PinSortOrder
}

/**
 * What a pin is called in a list: its own name, else its type's label, else the
 * raw type id made readable (`salt_stone` → `Salt stone`). Matches the app's
 * `pinDisplayName`, and matters more than it looks — a GPX import can drop 500
 * pins that carry no name at all, and "Waypoint" repeated 500 times is still
 * more use than 500 blank rows.
 */
export function pinDisplayName(
  pin: Pick<MapObject, 'label' | 'type'>,
  labelOf: PinTypeLabeler = IDENTITY_LABELER,
): string {
  const own = (pin.label ?? '').trim()
  if (own) return own

  const typeLabel = labelOf(pin.type)
  // labelOf falls back to the raw id for a type it doesn't know, which is the
  // case this humanising step is for.
  if (typeLabel && typeLabel !== pin.type) return typeLabel

  const humanised = pin.type.replace(/_/g, ' ')
  return humanised ? humanised[0].toUpperCase() + humanised.slice(1) : ''
}

/**
 * How an unnamed line is named from its kind. Supplied by the caller so the
 * fallback is TRANSLATED — the app has `pin_collections_line_name_fallback` for
 * exactly this, and a hardcoded English "Line" sitting in an otherwise
 * translated list is the papercut `MapLayersPicker` already documents.
 */
export type LineKindLabels = (kind: GeoLine['kind']) => string

/** The untranslated fallback, for the pure sort/filter path and for tests. Any
 *  rendering caller passes its own translated one. */
const DEFAULT_LINE_LABELS: LineKindLabels = (kind) =>
  kind === 'arrow' ? 'Arrow' : kind === 'trail' ? 'Trail' : 'Line'

/** What a line is called in a list: its own name, else its kind. */
export function lineDisplayName(
  line: Pick<GeoLine, 'name' | 'kind'>,
  labels: LineKindLabels = DEFAULT_LINE_LABELS,
): string {
  const own = (line.name ?? '').trim()
  return own || labels(line.kind)
}

/** Name comparison shared by the pin and line sorts: case-insensitive, and an
 *  item with no name of its own sorts last however the list is ordered. */
function compareNames(a: string, b: string, order: PinSortOrder): number {
  const na = a.trim().toLowerCase()
  const nb = b.trim().toLowerCase()
  if (!na && !nb) return 0
  if (!na) return 1
  if (!nb) return -1
  return order === 'descending' ? nb.localeCompare(na) : na.localeCompare(nb)
}

/**
 * Sorts pins by display name. Note this reads the DISPLAY name, not the stored
 * one: a list of rows reading "Camera", "Stand", "Tower" sorted by their
 * (empty) stored names would look unsorted to the person reading it.
 */
export function sortPins(
  pins: readonly MapObject[],
  order: PinSortOrder,
  labelOf: PinTypeLabeler = IDENTITY_LABELER,
): MapObject[] {
  return [...pins].sort((a, b) => compareNames(pinDisplayName(a, labelOf), pinDisplayName(b, labelOf), order))
}

/**
 * The initial pin order: the hunter's own arrangement when there is one, else
 * A–Z. "When there is one" is *any* pin carrying an `orderIndex` — a folder
 * that was hand-ordered on the phone keeps that order on web, and pins added
 * since (which carry none) fall to the end rather than scattering through it.
 */
export function sortPinsInitial(
  pins: readonly MapObject[],
  labelOf: PinTypeLabeler = IDENTITY_LABELER,
): MapObject[] {
  if (!pins.some((p) => p.orderIndex != null)) return sortPins(pins, 'ascending', labelOf)
  return [...pins].sort((a, b) => compareByOrderThenName(a.orderIndex, b.orderIndex, pinDisplayName(a, labelOf), pinDisplayName(b, labelOf)))
}

/** Lines always follow their stored order then their name — the app offers no
 *  A–Z toggle for lines, and inventing one here would put the two lists in a
 *  folder under different rules. */
export function sortLines(lines: readonly GeoLine[], labels?: LineKindLabels): GeoLine[] {
  return [...lines].sort((a, b) =>
    compareByOrderThenName(a.orderIndex, b.orderIndex, lineDisplayName(a, labels), lineDisplayName(b, labels)),
  )
}

function compareByOrderThenName(
  orderA: number | undefined,
  orderB: number | undefined,
  nameA: string,
  nameB: string,
): number {
  if (orderA != null && orderB != null) return orderA - orderB
  if (orderA != null) return -1
  if (orderB != null) return 1
  return compareNames(nameA, nameB, 'ascending')
}

/**
 * Filters a list by what the user typed.
 *
 * **Deliberate divergence from the app.** The app filters on the pin's *stored*
 * name, so typing "sta" there matches nothing among rows that visibly read
 * "Stand" — every unnamed pin is unsearchable even though its type label is
 * right there on screen. Web filters on the same string it renders. This is a
 * local view filter: nothing is written, and no cross-platform contract reads
 * it, so matching the app's behaviour here would only reproduce a papercut.
 *
 * Prefix rather than substring IS the app's rule, and is kept: a hunting ground
 * has folders of near-identical names ("Passipaikat", "Peurapassit") and prefix
 * matching is what makes typing three letters narrow them.
 */
function filterByName<T>(items: readonly T[], query: string, nameOf: (item: T) => string): T[] {
  const q = query.trim().toLowerCase()
  if (!q) return [...items]
  return items.filter((item) => nameOf(item).trim().toLowerCase().startsWith(q))
}

/**
 * Groups pins and lines under their folders, split into the team's collection
 * and the viewer's own — see {@link PinFolderCollection} for why the two are
 * never merged into one list.
 *
 * `previousSortOrder` is the order carried over from an earlier render, or
 * undefined on first build. When it is `custom` or undefined the initial
 * resolution runs (custom iff any pin carries an `orderIndex`); an explicit
 * `ascending`/`descending` is honoured as asked.
 *
 * A pin lands in its collection's `ungroupedPins` when it carries no
 * `folderId`, when the folder it names is not in `folders`, **or** when that
 * folder belongs to the other collection. The second case is not defensive
 * padding: it is what a pin looks like the moment its folder is deleted, and
 * dropping such pins from the list instead would make a hunter's pins appear to
 * vanish when they are still on the map.
 */
export function buildPinFolderView({
  pins,
  lines = [],
  folders,
  searchQuery = '',
  previousSortOrder,
  lineLabels,
  labelOf = IDENTITY_LABELER,
}: {
  pins: readonly MapObject[]
  lines?: readonly GeoLine[]
  folders: readonly PinFolder[]
  searchQuery?: string
  previousSortOrder?: PinSortOrder
  /** Translated names for unnamed lines — see {@link LineKindLabels}. Sorting
   *  and searching read the same string the row will show. */
  lineLabels?: LineKindLabels
  /** The caller's pin-type catalog labeler, so an unnamed pin sorts, filters
   *  and renders by its LOCALIZED type name — the same string the row shows.
   *  Defaults to the identity labeler for the pure/test path. */
  labelOf?: PinTypeLabeler
}): PinFolderView {
  const nameOfPin = (p: MapObject) => pinDisplayName(p, labelOf)
  let sortedPins: MapObject[]
  let sortOrder: PinSortOrder
  if (previousSortOrder == null || previousSortOrder === 'custom') {
    sortedPins = sortPinsInitial(pins, labelOf)
    sortOrder = pins.some((p) => p.orderIndex != null) ? 'custom' : 'ascending'
  } else {
    sortedPins = sortPins(pins, previousSortOrder, labelOf)
    sortOrder = previousSortOrder
  }

  const nameOfLine = (l: GeoLine) => lineDisplayName(l, lineLabels)
  const filteredPins = filterByName(sortedPins, searchQuery, nameOfPin)
  const filteredLines = filterByName(sortLines(lines, lineLabels), searchQuery, nameOfLine)

  // Keyed on the (collection, id) PAIR, never the id alone. A folder id is
  // unique only within its own collection, so a team folder and a private
  // folder can carry the same one — keying on the id would file one hunter's
  // pins under a name their team chose.
  const key = (isPrivate: boolean, folderId: string) => `${isPrivate ? 'p' : 't'}:${folderId}`
  const known = new Set(folders.map((f) => key(f.private === true, f.id)))
  const filed = (item: { folderId?: string; isPrivate?: boolean }) =>
    item.folderId != null && known.has(key(item.isPrivate === true, item.folderId))

  const collection = (isPrivate: boolean): PinFolderCollection => {
    const ownPins = filteredPins.filter((p) => (p.isPrivate === true) === isPrivate)
    // Lines all belong to the team: nothing writes an owner onto one, so the
    // private collection is pins only.
    const ownLines = isPrivate ? [] : filteredLines
    return {
      private: isPrivate,
      // Folder order is the owner document's array order — not re-sorted here.
      folders: folders
        .filter((f) => (f.private === true) === isPrivate)
        .map((folder) => ({
          folder,
          pins: ownPins.filter((p) => p.folderId === folder.id),
          lines: ownLines.filter((l) => l.folderId === folder.id),
        })),
      ungroupedPins: ownPins.filter((p) => !filed(p)),
      ungroupedLines: ownLines.filter((l) => !filed(l)),
    }
  }

  return { team: collection(false), mine: collection(true), sortOrder }
}
